import { useEffect, useState } from "react";
import { Atom } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useTipCelebration } from "@/components/chat/TipCelebration";
import { openTweet } from "@/components/common/ShareToX";
import { BandBadgeCard } from "@/components/common/BandBadgeCard";
import { SCORE_BANDS, bandIndexForScore } from "@/lib/trustBands";

const KEY = "bloq_trustband_seen";
const CELEBRATE_FROM = 2; // Baseline (55) and above — skip Restricted/New
const SHARE_BASE = process.env.REACT_APP_SHARE_BASE || "https://bloqtalk.app";

const BAND_COPY = {
  Baseline: "You're now a trusted member — easier to discover and message more people.",
  Trusted: "Top-tier reputation. Your messages skip more filters and more gates open up.",
  "High Trust": "The highest trust tier on BLOQ. You're in rare company 🏆",
};

// App-wide watcher: when the user's BLOQ Score crosses UP into a new band, pop a congrats card
// + confetti. Fires once per upgrade (localStorage-guarded); first observation just records the
// baseline so existing users aren't celebrated on load.
export function TrustBandWatcher() {
  const { user } = useAuth();
  const { celebrate } = useTipCelebration();
  const [open, setOpen] = useState(false);
  const [band, setBand] = useState(null);

  useEffect(() => {
    if (!user || typeof user.score !== "number") return;
    const idx = bandIndexForScore(user.score);
    let prev = null;
    try {
      const v = localStorage.getItem(KEY);
      prev = v === null ? null : parseInt(v, 10);
    } catch {
      /* ignore */
    }
    if (prev === null) {
      try { localStorage.setItem(KEY, String(idx)); } catch { /* ignore */ }
      return;
    }
    if (idx === prev) return;
    try { localStorage.setItem(KEY, String(idx)); } catch { /* ignore */ }
    if (idx > prev && idx >= CELEBRATE_FROM) {
      const name = SCORE_BANDS[idx].name;
      setBand(name);
      setOpen(true);
      celebrate({ variant: "founding", title: `You reached ${name}!`, subtitle: `Your BLOQ Score is now ${user.score}` });
    }
  }, [user?.score, user, celebrate]);

  if (!band) return null;

  const shareUrl = `${SHARE_BASE}/api/u/${encodeURIComponent(user?.username || "")}?s=${user?.score || 0}&b=${encodeURIComponent(band)}&v=${user?.verified ? 1 : 0}`;
  const tweet = `I just reached ${band} on BLOQ Talk 🛡️ — BLOQ Score ${user?.score}. QRNG-seeded keys + post-quantum Cipher Lock: the only quantum-secured messenger for Web3. Every stranger screened, every scam blocked.`;

  // One-tap: native share sheet on mobile (any app), X compose fallback on desktop. The shared
  // link unfurls into the QRNG "trust passport" OG card (/api/u/<handle> → /api/og/trust/<h>.png).
  const shareCard = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: `BLOQ Talk — ${band}`, text: tweet, url: shareUrl });
      } catch { /* user cancelled or unsupported — no fallback needed */ }
      return;
    }
    openTweet({ text: tweet, url: shareUrl });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-sm border-bloq-mint/30 bg-bloq-s1" data-testid="trustband-celebrate-dialog">
        <DialogHeader>
          <DialogTitle className="text-center">Trust level up! 🎉</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3 py-1 text-center">
          <p className="text-sm text-bloq-muted" data-testid="trustband-name">{BAND_COPY[band] || `You've reached ${band}.`}</p>
          <BandBadgeCard band={band} score={user?.score} />
          <button
            onClick={shareCard}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-bloq-purple/40 bg-bloq-purple/15 py-3 text-sm font-bold text-bloq-purple transition-colors hover:bg-bloq-purple/25"
            data-testid="trustband-share-card-btn"
          >
            <Atom className="h-4 w-4" /> Share my trust card
          </button>
          <button
            onClick={() => setOpen(false)}
            className="text-sm font-semibold text-bloq-muted transition-colors hover:text-white"
            data-testid="trustband-dismiss-btn"
          >
            Keep it up
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
